import React from 'react';
import { FiTarget } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const AbsoluteValueLesson = () => {
  const sections = [
    {
      id: 'absolute-value-as-distance',
      title: 'Absolute Value as Distance',
      content: {
        introduction:
          'Absolute value measures distance from zero. Because distance is never negative, an absolute value expression is always zero or positive.',

        concepts: [
          {
            title: 'Core Definition',
            explanation:
              '§|a|§ is the distance between §a§ and §0§ on the number line. So §|7| = 7§ and §|-7| = 7§.',
            example: {
              problem: 'Evaluate: §|3 - 10|§',
              solution: [
                'Simplify inside the bars first: §3 - 10 = -7§',
                'Take the distance from zero: §|-7| = 7§',
                'Answer: §7§',
              ],
            },
          },
          {
            title: 'Distance Between Two Numbers',
            explanation:
              '§|x - a|§ means the distance between §x§ and §a§. This interpretation makes many GRE problems faster.',
            example: {
              problem: 'What does §|x - 4| = 6§ mean?',
              solution: [
                '§x§ is 6 units away from 4',
                'Move 6 units right: §x = 4 + 6 = 10§',
                'Move 6 units left: §x = 4 - 6 = -2§',
                'Solutions: §x = -2§ or §x = 10§',
              ],
            },
          },
          {
            title: 'Always Check the Inside',
            explanation:
              'Absolute value does not mean "make every term positive." First simplify the entire expression inside the bars.',
            example: {
              problem: 'Evaluate: §|-5 + 2|§',
              solution: [
                'Simplify inside first: §-5 + 2 = -3§',
                'Then take absolute value: §|-3| = 3§',
                'Do not rewrite §|-5 + 2|§ as §5 + 2§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'absolute-value-equations',
      title: 'Absolute Value Equations',
      content: {
        introduction:
          'An equation like §|expression| = positive number§ usually splits into two linear equations. One case is positive; the other is negative.',

        concepts: [
          {
            title: 'Two-Case Rule',
            explanation:
              'If §|A| = c§ and §c > 0§, then §A = c§ or §A = -c§.',
            example: {
              problem: 'Solve: §|2x - 3| = 9§',
              solution: [
                'Split into two cases: §2x - 3 = 9§ or §2x - 3 = -9§',
                'Case 1: §2x = 12§, so §x = 6§',
                'Case 2: §2x = -6§, so §x = -3§',
                'Solutions: §x = -3§ or §x = 6§',
              ],
            },
          },
          {
            title: 'No Solution Cases',
            explanation:
              'An absolute value cannot equal a negative number. If the isolated absolute value equals a negative value, there is no solution.',
            example: {
              problem: 'Solve: §|x + 4| = -2§',
              solution: [
                '§|x + 4|§ is always nonnegative',
                'A nonnegative expression cannot equal §-2§',
                'Answer: no solution',
              ],
            },
          },
          {
            title: 'Isolate Before Splitting',
            explanation:
              'Do not split until the absolute value expression is isolated on one side of the equation.',
            example: {
              problem: 'Solve: §3|x - 2| + 1 = 13§',
              solution: [
                'Subtract 1: §3|x - 2| = 12§',
                'Divide by 3: §|x - 2| = 4§',
                'Split: §x - 2 = 4§ or §x - 2 = -4§',
                'Solutions: §x = 6§ or §x = -2§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'two-absolute-values',
      title: 'Two Absolute Values',
      content: {
        introduction:
          'Some GRE equations compare two absolute value expressions or add them together. Distance thinking still helps, but you may need to split the number line into intervals.',

        concepts: [
          {
            title: 'Equal Distances: §|A| = |B|§',
            explanation:
              'If two absolute values are equal, the inside expressions are either equal or opposites: §A = B§ or §A = -B§. Geometrically, §x§ is equidistant from two points.',
            example: {
              problem: 'Solve: §|x - 2| = |x + 6|§',
              solution: [
                '§x§ is the same distance from 2 as from -6',
                'The midpoint is §(-6 + 2)/2 = -2§',
                'Algebra check: §x - 2 = x + 6§ has no solution',
                'Or §x - 2 = -(x + 6)§ → §x - 2 = -x - 6§ → §2x = -4§ → §x = -2§',
                'Solution: §x = -2§',
              ],
            },
          },
          {
            title: 'Sum of Absolute Values',
            explanation:
              'For §|x - a| + |x - b| = c§, find the breakpoints §a§ and §b§, then write a separate equation on each interval of the number line.',
            example: {
              problem: 'Solve: §|x - 3| + |x + 1| = 10§',
              solution: [
                'Breakpoints at §x = -1§ and §x = 3§',
                'If §x ≥ 3§: §(x - 3) + (x + 1) = 10§ → §2x - 2 = 10§ → §x = 6§',
                'If §x ≤ -1§: §(3 - x) + (-x - 1) = 10§ → §2 - 2x = 10§ → §x = -4§',
                'On §(-1, 3)§ the sum is constant at 4, so no solution there',
                'Solutions: §x = -4§ or §x = 6§',
              ],
            },
          },
          {
            title: '§|x| + |y|§ vs §|x + y|§',
            explanation:
              'For real numbers, §|x + y| ≤ |x| + |y|§ always. Equality holds when §x§ and §y§ have the same sign (or one is zero). Different signs usually make §|x + y|§ smaller.',
            example: {
              problem: 'If §x = 2§ and §y = -5§, compare §|x| + |y|§ and §|x + y|§',
              solution: [
                '§|x| + |y| = 2 + 5 = 7§',
                '§x + y = 2 + (-5) = -3§, so §|x + y| = 3§',
                'Different signs made the combined absolute value smaller',
                'Here §|x| + |y| > |x + y|§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'less-than-inequalities',
      title: 'Less-Than Inequalities',
      content: {
        introduction:
          'Absolute value inequalities with less than signs describe numbers inside a distance band. The solution is usually a single interval.',

        concepts: [
          {
            title: 'Inside the Boundary',
            prerequisiteNote:
              'First isolate the absolute value expression. Then turn the distance statement into a compound inequality.',
            vocabulary: ['boundary', 'compound inequality', 'endpoint', 'inclusive'],
            explanation:
              'If §|A| < c§ and §c > 0§, then §-c < A < c§. For §≤§, include the endpoints.',
            microExample: {
              problem: 'Solve §|x| ≤ 4§',
              solution: [
                '§|x| ≤ 4§ means x is at most 4 units from 0.',
                'So §-4 ≤ x ≤ 4§.',
              ],
            },
            greTrap:
              'For less-than absolute value inequalities, write one middle expression between two boundaries: §-c ≤ A ≤ c§.',
            checkQuestion:
              'What compound inequality matches §|4x - 28| ≤ 10§?',
            example: {
              problem: 'Solve: §|x - 5| < 3§',
              solution: [
                'This means §x§ is less than 3 units away from 5',
                'Write the compound inequality: §-3 < x - 5 < 3§',
                'Add 5 to all parts: §2 < x < 8§',
                'Solution: §(2, 8)§',
              ],
            },
          },
          {
            title: 'Inclusive Endpoints',
            explanation:
              'When the inequality is §≤§, the boundary points are included.',
            example: {
              problem: 'Solve: §|2x + 1| ≤ 7§',
              solution: [
                'Convert to compound form: §-7 ≤ 2x + 1 ≤ 7§',
                'Subtract 1: §-8 ≤ 2x ≤ 6§',
                'Divide by 2: §-4 ≤ x ≤ 3§',
                'Solution: §[-4, 3]§',
              ],
            },
          },
          {
            title: 'Immediate No Solution',
            explanation:
              'If §|A| < negative number§ or §|A| ≤ negative number§, there is no solution because absolute value cannot be negative.',
            example: {
              problem: 'Solve: §|x - 1| < -4§',
              solution: [
                '§|x - 1|§ is always at least 0',
                'It cannot be less than §-4§',
                'Answer: no solution',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'greater-than-inequalities',
      title: 'Greater-Than Inequalities',
      content: {
        introduction:
          'Absolute value inequalities with greater than signs describe numbers outside a distance band. The solution usually has two separate intervals.',

        concepts: [
          {
            title: 'Outside the Boundary',
            explanation:
              'If §|A| > c§ and §c > 0§, then §A > c§ or §A < -c§. For §≥§, include the endpoints.',
            example: {
              problem: 'Solve: §|x + 2| > 5§',
              solution: [
                'Split into outside cases: §x + 2 > 5§ or §x + 2 < -5§',
                'Case 1: §x > 3§',
                'Case 2: §x < -7§',
                'Solution: §x < -7§ or §x > 3§',
              ],
            },
          },
          {
            title: 'The OR Pattern',
            explanation:
              'Greater-than absolute value inequalities almost always produce an "or" solution. Less-than inequalities produce an "and" interval.',
            example: {
              problem: 'Solve: §|3x - 6| ≥ 12§',
              solution: [
                'Split: §3x - 6 ≥ 12§ or §3x - 6 ≤ -12§',
                'Case 1: §3x ≥ 18§, so §x ≥ 6§',
                'Case 2: §3x ≤ -6§, so §x ≤ -2§',
                'Solution: §x ≤ -2§ or §x ≥ 6§',
              ],
            },
          },
          {
            title: 'Counting Integer Solutions',
            explanation:
              'The GRE often asks how many integers satisfy an absolute value condition. Translate the inequality first, then count carefully.',
            example: {
              problem: 'How many integers satisfy §|x - 1| ≤ 4§?',
              solution: [
                'Convert: §-4 ≤ x - 1 ≤ 4§',
                'Add 1: §-3 ≤ x ≤ 5§',
                'Integers: §-3, -2, -1, 0, 1, 2, 3, 4, 5§',
                'Answer: §9§ integers',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'gre-strategy',
      title: 'GRE Strategy and Traps',
      content: {
        introduction:
          'Absolute value problems are often short, but the traps are predictable: forgetting a case, splitting too early, or mixing up "and" and "or."',

        concepts: [
          {
            title: 'Use the Number Line',
            explanation:
              'When the expression is simple, think in terms of distance. This reduces algebra and makes endpoint mistakes easier to spot.',
            example: {
              problem: 'Which values satisfy §|x - 3| < 2§?',
              solution: [
                '§x§ must be within 2 units of 3',
                'The endpoints are §1§ and §5§',
                'Because the sign is §<§, endpoints are not included',
                'Solution: §1 < x < 5§',
              ],
            },
          },
          {
            title: 'Plug In to Check',
            explanation:
              'After solving, test a value from each interval and each endpoint if the answer choices are close.',
            example: {
              problem: 'Check the solution §x < -1§ or §x > 5§ for §|x - 2| > 3§',
              solution: [
                'Test §x = 0§, inside the gap: §|0 - 2| = 2§, not greater than 3',
                'Test §x = 6§: §|6 - 2| = 4§, works',
                'Test §x = -2§: §|-2 - 2| = 4§, works',
                'The outside intervals are correct',
              ],
            },
          },
          {
            title: 'Quantitative Comparison Shortcut',
            explanation:
              'For QC questions, compare the range of possible values instead of solving for one value if the equation or inequality allows multiple values.',
            example: {
              problem: 'If §|x - 4| = 3§, compare Quantity A: §x§ and Quantity B: §4§',
              solution: [
                'Solutions are §x = 1§ or §x = 7§',
                'If §x = 1§, Quantity B is greater',
                'If §x = 7§, Quantity A is greater',
                'Answer: the relationship cannot be determined',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 75,
      question: 'Solve: §|x - 4| = 6§',
      options: [
        '§x = -2§ or §x = 10§',
        '§x = 2§ or §x = 10§',
        '§x = -10§ or §x = 2§',
        '§x = 6§ only',
        'No solution',
      ],
      correct: 0,
      explanation:
        '§|x - 4| = 6§ means §x - 4 = 6§ or §x - 4 = -6§. Thus §x = 10§ or §x = -2§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'Solve: §3|2x + 1| - 5 = 16§',
      options: [
        '§x = 3§ or §x = -4§',
        '§x = 4§ or §x = -3§',
        '§x = 3§ only',
        '§x = -4§ only',
        'No solution',
      ],
      correct: 0,
      explanation:
        'Add 5 and divide by 3: §|2x + 1| = 7§. Then §2x + 1 = 7§ gives §x = 3§, and §2x + 1 = -7§ gives §x = -4§.',
    },
    {
      type: 'timed',
      timeLimit: 80,
      question: 'Solve: §|x + 3| < 5§',
      options: [
        '§-8 < x < 2§',
        '§x < -8§ or §x > 2§',
        '§-2 < x < 8§',
        '§x < -2§ or §x > 8§',
        '§-8 ≤ x ≤ 2§',
      ],
      correct: 0,
      explanation:
        'Convert to §-5 < x + 3 < 5§. Subtract 3 throughout: §-8 < x < 2§.',
    },
    {
      type: 'timed',
      timeLimit: 95,
      question: 'Solve: §|2x - 5| ≥ 9§',
      options: [
        '§x ≤ -2§ or §x ≥ 7§',
        '§-2 ≤ x ≤ 7§',
        '§x < -2§ or §x > 7§',
        '§x ≤ 2§ or §x ≥ 7§',
        'No solution',
      ],
      correct: 0,
      explanation:
        'Split into §2x - 5 ≥ 9§ or §2x - 5 ≤ -9§. This gives §x ≥ 7§ or §x ≤ -2§.',
    },
    {
      type: 'timed',
      timeLimit: 85,
      question: 'How many integers satisfy §|x - 2| ≤ 3§?',
      options: ['§7§', '§6§', '§5§', '§4§', '§8§'],
      correct: 0,
      explanation:
        '§-3 ≤ x - 2 ≤ 3§, so §-1 ≤ x ≤ 5§. The integers are §-1, 0, 1, 2, 3, 4, 5§, for a total of 7.',
    },
    {
      type: 'timed',
      timeLimit: 75,
      question: 'Which equation has no real solution?',
      options: [
        '§|x - 1| = -4§',
        '§|x - 1| = 0§',
        '§|x - 1| = 4§',
        '§|x - 1| < 4§',
        '§|x - 1| > 4§',
      ],
      correct: 0,
      explanation:
        'An absolute value expression cannot equal a negative number, so §|x - 1| = -4§ has no real solution.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'Solve: §|x - 2| = |x + 6|§',
      options: [
        '§x = -2§',
        '§x = 2§',
        '§x = 6§',
        '§x = -6§',
        'No solution',
      ],
      correct: 0,
      explanation:
        '§x§ is equidistant from 2 and -6, so §x = -2§. Algebra: §x - 2 = -(x + 6)§ gives the same result.',
    },
    {
      type: 'untimed',
      question: 'Solve: §|x - 3| + |x + 1| = 10§',
      options: [
        '§x = -4§ or §x = 6§',
        '§x = 3§ or §x = -1§',
        '§x = 4§ or §x = -3§',
        '§x = 6§ only',
        'No solution',
      ],
      correct: 0,
      explanation:
        'For §x ≥ 3§, §2x - 2 = 10§ gives §x = 6§. For §x ≤ -1§, §2 - 2x = 10§ gives §x = -4§. The middle interval sums to 4, not 10.',
    },
    {
      type: 'untimed',
      question: 'Solve: §|4 - x| = 3§',
      options: [
        '§x = 1§ or §x = 7§',
        '§x = -1§ or §x = 7§',
        '§x = 1§ or §x = -7§',
        '§x = 3§ or §x = 4§',
        'No solution',
      ],
      correct: 0,
      explanation:
        '§|4 - x| = 3§ means the distance between §x§ and 4 is 3. So §x = 1§ or §x = 7§.',
    },
    {
      type: 'untimed',
      question: 'Solve: §|3x + 6| < 12§',
      options: [
        '§-6 < x < 2§',
        '§x < -6§ or §x > 2§',
        '§-2 < x < 6§',
        '§x < -2§ or §x > 6§',
        '§-6 ≤ x ≤ 2§',
      ],
      correct: 0,
      explanation:
        '§-12 < 3x + 6 < 12§. Subtract 6: §-18 < 3x < 6§. Divide by 3: §-6 < x < 2§.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'If §|x - 5| = 2§, compare the two quantities.',
      quantityA: '§x§',
      quantityB: '§5§',
      correct: 3,
      explanation:
        'The solutions are §x = 3§ or §x = 7§. For §x = 3§, Quantity B is greater; for §x = 7§, Quantity A is greater. The relationship cannot be determined.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question: 'Which values satisfy §|x + 1| ≤ 2§? Select all that apply.',
      options: ['§-4§', '§-3§', '§-1§', '§1§', '§2§'],
      correct: [1, 2, 3],
      explanation:
        '§|x + 1| ≤ 2§ becomes §-2 ≤ x + 1 ≤ 2§, so §-3 ≤ x ≤ 1§. The listed values that work are §-3§, §-1§, and §1§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      questionType: 'numeric_entry',
      question: 'How many integer values of §x§ satisfy §|x| < 4§?',
      correctAnswer: '7',
      acceptedAnswers: ['7'],
      explanation:
        '§|x| < 4§ means §-4 < x < 4§. The integers are §-3, -2, -1, 0, 1, 2, 3§, so there are 7.',
    },
    {
      type: 'untimed',
      question: 'Solve: §|x - 3| > 0§',
      options: [
        'All real §x§ except §3§',
        'All real §x§',
        '§x = 3§ only',
        '§x > 3§ only',
        '§x < 3§ only',
      ],
      correct: 0,
      explanation:
        '§|x - 3| = 0§ only when §x = 3§. Therefore §|x - 3| > 0§ for every real §x§ except §3§.',
    },
  ];

  const quickReference = [
    'Meaning: §|x - a|§ is the distance between §x§ and §a§',
    'Equation: §|A| = c§ with §c > 0§ gives §A = c§ or §A = -c§',
    'Two bars equal: §|A| = |B|§ gives §A = B§ or §A = -B§',
    'Sum of bars: split the number line at each breakpoint, then solve interval by interval',
    'Triangle inequality: §|x + y| ≤ |x| + |y|§; equal when x and y have the same sign',
    'No solution: §|A| = negative number§',
    'Less than: §|A| < c§ gives §-c < A < c§',
    'Less than or equal: §|A| ≤ c§ gives §-c ≤ A ≤ c§',
    'Greater than: §|A| > c§ gives §A < -c§ or §A > c§',
    'Greater than or equal: §|A| ≥ c§ gives §A ≤ -c§ or §A ≥ c§',
    'GRE shortcut: less than = inside interval; greater than = outside intervals',
  ];

  const getStartedWithProps = {
    title: 'Need help with Absolute Value?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding absolute value equations and inequalities for GRE Quant',
    aicontext:
      'Master absolute value as distance, equations, inequalities, interval notation, integer counting, and GRE Quant traps',
  };

  return (
    <LessonTemplate
      lessonId="absolute-value"
      lessonTitle="Absolute Value Equations & Inequalities"
      lessonIcon={<FiTarget />}
      lessonDescription="Master absolute value as distance, then solve equations and inequalities without losing cases"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="absolute-value" />
      }
    />
  );
};

export default AbsoluteValueLesson;
